import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type EmptyStateProps = {
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  /** Button or link (e.g. "Retour à la liste"). */
  action?: ReactNode;
  /** `error`: a page or block that could not be shown ("Outil introuvable"). */
  tone?: 'neutral' | 'error';
  /** `compact`: less padding, for empty lists inside a card. */
  size?: 'default' | 'compact';
  className?: string;
  'data-testid'?: string;
};

/** Dashed placeholder for "nothing here yet" and "not found" (messages use `Callout`). */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  tone = 'neutral',
  size = 'default',
  className,
  'data-testid': testId,
}: EmptyStateProps) {
  return (
    <div
      data-testid={testId}
      role={tone === 'error' ? 'alert' : undefined}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed text-center',
        size === 'compact' ? 'px-4 py-6' : 'px-6 py-10',
        tone === 'error' ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-muted/30',
        className
      )}
    >
      {Icon && (
        <div
          className={cn(
            'mb-1 flex h-10 w-10 items-center justify-center rounded-xl',
            tone === 'error'
              ? 'bg-destructive/10 text-destructive'
              : 'bg-muted text-muted-foreground'
          )}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </div>
      )}
      <p className="text-sm font-semibold text-foreground">{title}</p>
      {description && <p className="max-w-md text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-2 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}
